import type { PaletteOptions } from "@mui/material";

const computedStyles = getComputedStyle(document.documentElement);

const brand = {
  purple: computedStyles.getPropertyValue("--color-brand"),
  purpleHover: computedStyles.getPropertyValue("--color-brand-hover"),
  orange: computedStyles.getPropertyValue("--color-brand-secondary"),
};
type BrandColors = keyof typeof brand;

const text = {
  black: computedStyles.getPropertyValue("--color-black"),
  lightGray: computedStyles.getPropertyValue("--color-light-gray"),
  mediumGray: computedStyles.getPropertyValue("--color-medium-gray"),
  gray: computedStyles.getPropertyValue("--color-gray"),
};
type TextColors = keyof typeof text;

const background = {
  white: computedStyles.getPropertyValue("--color-white"),
  lightBlue: computedStyles.getPropertyValue("--color-background"),
};
type BackgroundColors = keyof typeof background;

const tags = {
  blue: computedStyles.getPropertyValue("--color-tags-blue"),
  red: computedStyles.getPropertyValue("--color-tags-red"),
  green: computedStyles.getPropertyValue("--color-tags-green"),
  yellow: computedStyles.getPropertyValue("--color-tags-yellow"),
  purple: computedStyles.getPropertyValue("--color-tags-purple"),
};

const accent = {
  lightBlue: computedStyles.getPropertyValue("--color--accent-light-blue"),
  lightRed: computedStyles.getPropertyValue("--color--accent-light-red"),
  lightGreen: computedStyles.getPropertyValue("--color--accent-light-green"),
  lightYellow: computedStyles.getPropertyValue("--color--accent-light-yellow"),
  lightPurple: computedStyles.getPropertyValue("--color--accent-light-purple"),
};

const alerts = {
  warning: computedStyles.getPropertyValue("--color-warning"),
  error: computedStyles.getPropertyValue("--color-error"),
  info: computedStyles.getPropertyValue("--color-info"),
  success: computedStyles.getPropertyValue("--color-success"),
};
type AlertColors = keyof typeof alerts;

const muifyColor = (color: string) => ({ main: color });
const muifyPalette = ({ brand, text, alerts, background, ...rest }: Omit<Palette, "tags">) =>
  ({
    brand,
    alerts,
    primary: muifyColor(brand.purple),
    secondary: muifyColor(brand.orange),
    text: { ...text, default: text.black },
    success: muifyColor(alerts.success),
    warning: muifyColor(alerts.warning),
    error: muifyColor(alerts.error),
    info: muifyColor(alerts.info),
    background: { ...background, default: background.lightBlue },
    common: { black: text.black, white: background.white },
    ...rest,
  }) as PaletteOptions;

const palette = { brand, text, background, alerts, accent, tags };
export default muifyPalette(palette);
export type Palette = typeof palette;

declare module "@mui/material" {
  interface Theme {
    palette: Palette;
  }
  interface CssThemeVariables {
    enabled: false;
  }
}

interface DisableMDColors {
  primary: false;
  secondary: false;
  success: false;
  error: false;
  info: false;
  warning: false;
  action: false;
}

type BrandColorOverrides = { [key in `brand.${BrandColors}`]: true };
type AlertColorOverrides = { [key in `alerts.${AlertColors}`]: true };
type TextColorOverrides = { [key in `text.${TextColors}`]: true };
type BackgroundColorOverrides = { [key in `background.${BackgroundColors}`]: true };

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides extends DisableMDColors {}
}
declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides extends DisableMDColors {}
}
declare module "@mui/material/ToggleButtonGroup" {
  interface ToggleButtonGroupPropsColorOverrides extends DisableMDColors, AlertColorOverrides {}
}
declare module "@mui/material/Checkbox" {
  interface CheckboxPropsColorOverrides extends DisableMDColors, BrandColorOverrides {}
}
declare module "@mui/material/Switch" {
  interface SwitchPropsColorOverrides extends DisableMDColors, AlertColorOverrides {}
}
declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides extends DisableMDColors {}
}
declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides
    extends DisableMDColors,
      BrandColorOverrides,
      AlertColorOverrides,
      BackgroundColorOverrides,
      TextColorOverrides {}
}
declare module "@mui/material/LinearProgress" {
  interface LinearProgressPropsColorOverrides extends DisableMDColors, BrandColorOverrides {}
}
declare module "@mui/material/CircularProgress" {
  interface CircularProgressPropsColorOverrides extends DisableMDColors, BrandColorOverrides {}
}
